import { useEffect, useState } from "react";
import { IControlAttributes, IPageLink, ITableLookup } from "../../../base/models";
import { useAuth } from "../../../../modules/auth";
import { useGetProperties } from "../../../base/hooks";
import { useLazyGetBookingsQuery } from "./properties/shuttleDetailsApi";
import { ControlType, FetchStatus, IEntity, Property } from "../../../base/types";
import { Registry } from "../../../base/register";
import { ShuttleBookingProperties } from "./properties";
import moment from "moment";
import { IShuttleBookings } from "./properties/shuttleBooking";
import { ProgressIndicator, Spinner } from "../../../widgets";
import { useGetLookupLabel } from "../typography/name-lookup/useGetLookupLabel";
import { useGetPayment } from "./properties/useGetPayment";
import { useDataContext } from "../../page/context";
import { PageLinkHelper } from "../../../base/helpers";
import { useNavigate } from "react-router-dom";
import { IFieldValue } from "../../../services/models";
import { useUpdateMutation } from "../../../services/data/tableDataApi";

export const ShowShuttleBookingsComponent = (props: IControlAttributes) => {
  const { control } = props;
  const { properties } = useGetProperties({ bindings: control.bindings });
  const navigateToOnCancel: IPageLink | undefined = properties[Property.CancelPageLink];
  const bookingStatus: number | undefined = properties[Property.BookingStatus];
  const latestRecord: boolean | undefined = properties[Property.LatestRecord];
  const tableLookup: ITableLookup = properties[Property.TableLookup] || {};
  const { setData } = useDataContext();
  const { user, siteId } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const [updateRecord] = useUpdateMutation();
  const [getBookings, { data: bookings, isLoading }] = useLazyGetBookingsQuery();


  useEffect(() => {
    if (!bookingStatus || !user?.contactId) return;

    getBookings({
      contactId: user?.contactId,
      bookingStatus: bookingStatus
    });
    // eslint-disable-next-line 
  }, [user, bookingStatus, getBookings]);

  const handleChangeStatus = (booking: string) => {
    const record: IEntity = {
      new_bookingstatus: 100000004,
    };

    setLoading(booking);
    saveFormData(record, booking)
      .then(data => {
        setData({ type: "Card", result: data, status: FetchStatus.Success });
        if (siteId && navigateToOnCancel && navigateToOnCancel.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnCancel));
        }
      })
      .catch(err => {
        const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
        console.error(message);
      })
      .finally(() => setLoading(null));
  };

  const saveFormData = (record: IEntity, booking: string): Promise<IEntity> => {
    const fields: IFieldValue[] = Object.entries(record)
      .filter(([_, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value, validate: true }));

    return updateRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "edtec365_booking", sysId: booking, data: fields, triggers: true })
      .unwrap()
      .catch(err => {
        const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
        console.error(message);
        return Promise.reject(message);
      });
  };

  const sortedBookings = bookings?.slice().sort((a, b) => {
    return new Date(a.createdOn) < new Date(b.createdOn) ? 1 : -1;
  });

  const displayBookings = latestRecord
    ? sortedBookings?.slice(0, 1)
    : sortedBookings;
  return (
    <>
      {isLoading && (
        <div className="text-center my-3">
          <Spinner className='me-2' show={isLoading} />
        </div>
      )}

      {!isLoading && displayBookings && displayBookings.length > 0 ? (
        displayBookings.map((item) => (
          <div key={item.bookingID} className="card p-3 mb-3 border border-dark rounded wrap">
            <BookingsDetails item={item} tableLookup={tableLookup} handleChangeStatus={handleChangeStatus} loading={loading} />
          </div>
        ))
      ) : (
        <div className="mt-5 text-center">
          No upcoming bookings. Book your shuttle now.
        </div>
      )}
    </>
  );
};

const BookingsDetails = ({ item, tableLookup, handleChangeStatus, loading }: { item: IShuttleBookings, tableLookup: ITableLookup, handleChangeStatus: (bookingId: string) => void, loading: string | null }) => {
  const { getLookupLabel } = useGetLookupLabel();
  const [name, setName] = useState<string>();
  const value = item.routeId;
  const { paymentId } = useGetPayment(item.id || '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);

  useEffect(() => {
    getLookupLabel({ tableLookup, value })
      .then(name => setName(name));
    // eslint-disable-next-line
  }, [tableLookup, value]);

  useEffect(() => {
    const bookingTime = moment(item.bookingDateandTime, "MM/DD/YYYY HH:mm:ss").add(5.5, 'hours');
    const currentTime = moment();
    const timeDiff = bookingTime.diff(currentTime, 'minutes');

    if (timeDiff < 15) {
      setIsCancelDisabled(true);
      setErrorMessage("Booking cannot be cancelled before 15 mins of departure");
    } else {
      setIsCancelDisabled(false);
      setErrorMessage(null);
    }
  }, [item.bookingDateandTime]);

  const bookingStatus = (status: number) => {
    switch (status) {
      case 365970000: return "Draft";
      case 100000001: return "Payment Failed";
      case 100000006: return "Scheduled";
      case 100000000: return "Completed";
      case 100000004: return "Cancelled";
      default: return "Unknown";
    }
  };

  const statusBadgeClasses = (status: number) => {
    switch (status) {
      case 100000006: // Scheduled
        return ["badge", "bg-primary", "text-white", "text-center"];
      case 100000000: // Completed
        return ["badge", "bg-success", "text-white", "text-center"];
      case 100000004: // Cancelled
        return ["badge", "bg-danger", "text-white", "text-center"];
      case 365970000: // Payment Due
        return ["badge", "bg-warning", "text-white", "text-center"];
      default:
        return ["badge", "bg-black", "text-dark", "text-center"];
    }
  };

  return (
    <>
      <div className="row g-3">
        <div className="col-6 col-lg mb-1 me-lg-5">
          <strong>Booking ID</strong><br />
          <div className="text-primary">{item.bookingID}</div>
        </div>

        <div className="col-6 col-lg-2 mb-1">
          <strong>Booking Date & Time</strong><br />
          {moment(item.bookingDateandTime, "MM/DD/YYYY HH:mm:ss")
            .add(5.5, 'hours')
            .format("DD MMM YYYY HH:mm")}
        </div>

        <div className="col-6 col-lg-2 mb-1">
          <strong>Transaction Number</strong><br />
          {paymentId?.paymentId}
        </div>

        <div className="col-6 col-lg mb-1 text-lg-center">
          <strong>Amount</strong><br />
          ₹{item.totalCost}
        </div>

        <div className="col-6 col-lg-2 mb-1 text-lg-center">
          <strong>Route</strong><br />
          <span className="d-block text-wrap">{name}</span>
        </div>

        <div className="col-6 col-lg mb-1 text-lg-center">
          <strong>Passengers</strong><br />
          {item.capacity}
        </div>

        <div className="col-4 col-lg mb-1 text-lg-center">
          <strong>Status</strong><br />
          <div className="d-inline-block">
            <div className={`me-2 mt-1 rounded-pill ${statusBadgeClasses(item.bookingStatus).join(' ')}`}>
              {bookingStatus(item.bookingStatus)}
            </div>
          </div>
        </div>

        {item.bookingStatus === 100000006 && (
          <>
            <div className="col-4 col-lg mb-1">
              <strong></strong><br />
              <div className="d-inline-block">
                <button
                  className={`btn btn-sm ${isCancelDisabled || loading === item.bookingID ? 'btn-secondary' : 'btn-warning'}`}
                  style={{
                    backgroundColor: isCancelDisabled ? 'grey' : '#ffc107',
                    color: 'white',
                    borderColor: 'transparent',
                  }}
                  disabled={isCancelDisabled || loading === item.bookingID}
                  onMouseEnter={(e) => {
                    if (!isCancelDisabled) {
                      e.currentTarget.style.backgroundColor = '#ffc107';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCancelDisabled) {
                      e.currentTarget.style.backgroundColor = '#ffc107';
                    }
                  }}
                  onClick={() => {
                    if (!isCancelDisabled) {
                      setSelectedBooking(item.id || '');
                      setShowConfirm(true);
                    }
                  }}
                >
                  {loading === item.id ? (
                    <ProgressIndicator show={true} message='Please wait...' />
                  ) : (
                    <>
                      <i className="bi bi-x-circle"></i> Cancel
                    </>
                  )}
                </button>
              </div>
              {showConfirm && (
                <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)', position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh', zIndex: '1050' }}>
                  <div className="modal-dialog modal-dialog-centered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', transform: 'translateY(-50px)' }}>
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title text-center">Confirm Cancellation</h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowConfirm(false)}
                        ></button>
                      </div>
                      <div className="modal-body text-center">
                        <p>Are you sure you want to cancel this Booking?</p>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-danger"
                          onClick={() => {
                            setShowConfirm(false);
                            if (selectedBooking) {
                              handleChangeStatus(selectedBooking);
                            }
                          }}
                        >
                          Yes
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowConfirm(false)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="col-4 col-lg mb-1">
              <strong></strong><br />
              <div className="d-inline-block">
                <a href={item.pdfURL} target="_blank" className="btn btn-primary btn-sm" rel="noreferrer">
                  <i className="bi bi-download"></i> Receipt
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {(errorMessage && item.bookingStatus === 100000006) &&
        <div className="text-danger mt-1">
          <p>{errorMessage}</p>
        </div>
      }
    </>


  );
};


Registry.addControl({
  id: "1e184da3-daeb-42bb-b382-fd81ba54bb1e",
  name: "Show Shuttle Bookings",
  type: ControlType.ShowShuttleBookings,
  properties: ShuttleBookingProperties,
  component: ShowShuttleBookingsComponent
});
